import type { FC } from 'react';
import { useMemo } from 'react';

import { useAuthRouting } from '@/features/authorization/navigation/useAuthRouting';
import { useAppState } from '@/features/authorization/provider/useAppState';
import { useAuth } from '@/features/authorization/provider/useAuth';
import { useChatsRouting } from '@/features/chats/navigation/useChatsRouting';
import { useExperimentsRouting } from '@/features/experiments/navigation/useExperimentsRouting';
import { useFeatureManagementRouting } from '@/features/feature-management/navigation/useFeatureManagementRouting';
import { useGeoAreaRouting } from '@/features/geo-area/navigation/useGeoAreaRouting';
import { useGroupsRouting } from '@/features/groups/navigation/useGroupsRouting';
import { useMediaRouting } from '@/features/media/navigation/useMediaRouting';
import { usePostEditorRouting } from '@/features/post-editor/navigation/usePostEditorRouting';
import { usePostsRouting } from '@/features/posts/navigation/usePostsRouting';
import { usePrivacyRouting } from '@/features/privacy/navigation/usePrivacyRouting';
import { useProfileSettingRouting } from '@/features/profile-setting/navigation/useProfileSettingRouting';
import { useStartRouting } from '@/features/start/navigation/useStartRouting';
import { useTourPreviewRouting } from '@/features/tutorial/navigation/useTourPreviewRouting';
import { NavigationBar } from '@/navigation/components/navbar/NavigationBar';
import { TabsRouting } from '@/navigation/router/TabRouting';
import { AppStack } from '@/navigation/stacks/appStack';
import { AppRoute } from '@/navigation/types/navigation';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

export const AppRouting: FC = () => {
  const theme = useAppTheme();

  const { isAuthenticated } = useAuth();
  const {
    canUseApp,
    hasProfile,
    hasRequiredAction,
    isNotReachableError,
    hasValidVersion,
    versionCheckResult
  } = useAppState();

  const { authScreens, registrationScreens, invalidVersionScreen } =
    useAuthRouting();
  const { startScreens } = useStartRouting();
  const { profileSettingScreens } = useProfileSettingRouting();
  const { geoAreaScreens } = useGeoAreaRouting();
  const { privacyScreens } = usePrivacyRouting();
  const { postEditorScreens } = usePostEditorRouting();
  const { groupsScreens } = useGroupsRouting();
  const { postsScreens } = usePostsRouting();
  const { featureManagementScreens } = useFeatureManagementRouting();
  const { chatsScreens } = useChatsRouting();
  const { experimentsScreens } = useExperimentsRouting();
  const { mediaScreens } = useMediaRouting();
  const { tourPreviewScreens } = useTourPreviewRouting();

  const initialRouteName = useMemo(() => {
    if (isNotReachableError) return AppRoute.Error;
    if (versionCheckResult?.supported === false) return AppRoute.InvalidVersion;
    if (isAuthenticated) {
      if (canUseApp) return;
      if (!hasProfile) return AppRoute.Registration;
      if (!hasValidVersion) return AppRoute.InvalidVersion;
      if (hasProfile && hasRequiredAction) return AppRoute.RequiredAction;
    } else return AppRoute.Login;
  }, [
    canUseApp,
    hasProfile,
    hasRequiredAction,
    hasValidVersion,
    isAuthenticated,
    isNotReachableError,
    versionCheckResult?.supported
  ]);

  /**
   * If initialRouteName is undefined, then user is logged in and can use the app based on onboarding status.
   * initialRouteName will be undefined so that deep links do not break (route was being set to login and then going to tabs)
   */
  const areProtectedRoutesEnabled = isEmptyOrNull(initialRouteName);

  const authGroupScreens = useMemo(() => {
    if (initialRouteName === AppRoute.InvalidVersion)
      return invalidVersionScreen;
    if (isAuthenticated) return registrationScreens;
    return authScreens;
  }, [
    authScreens,
    initialRouteName,
    invalidVersionScreen,
    isAuthenticated,
    registrationScreens
  ]);

  return (
    <AppStack.Navigator initialRouteName={initialRouteName}>
      {areProtectedRoutesEnabled ? (
        <AppStack.Group
          screenOptions={{
            header: ({ options }) => <NavigationBar title={options.title} />,
            contentStyle: {
              backgroundColor: theme.colors.surfaceContainerLowest
            }
          }}
        >
          <AppStack.Screen
            name={AppRoute.Tabs}
            component={TabsRouting}
            options={{ headerShown: false }}
          />
          {startScreens}
          {geoAreaScreens}
          {profileSettingScreens}
          {privacyScreens}
          {postEditorScreens}
          {groupsScreens}
          {featureManagementScreens}
          {chatsScreens}
          {postsScreens}
          {mediaScreens}
          {/* TODO: proper dev screen, deactivate/remove experiments screen */}
          {experimentsScreens}
        </AppStack.Group>
      ) : (
        <AppStack.Group
          navigationKey={
            initialRouteName === AppRoute.InvalidVersion
              ? AppRoute.InvalidVersion
              : 'auth'
          }
          screenOptions={{ headerShown: false }}
        >
          {authGroupScreens}
          {/* DEV ONLY: /tour-preview, empty outside __DEV__ */}
          {initialRouteName === AppRoute.InvalidVersion || isAuthenticated
            ? []
            : tourPreviewScreens}
          {privacyScreens}
          {initialRouteName !== AppRoute.InvalidVersion && isAuthenticated
            ? geoAreaScreens
            : []}
        </AppStack.Group>
      )}
    </AppStack.Navigator>
  );
};
